import { useRef, useState, type FormEvent } from "react";
import { DictationTextarea } from "@/components/ui/DictationTextarea";
import type { NewProspect, ProductKey, Prospect, ProspectStatus } from "@shared/schema";
import { PROSPECT_STATUSES } from "@shared/schema";
import { PROSPECT_STATUS_LABELS } from "@shared/labels";
import { PRODUCT_LIST } from "@shared/products";

interface InternalState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  source: string;
  status: ProspectStatus;
  productInterest: "" | ProductKey;
  context: string;
  nextFollowUpAt: string;
}

function toState(p?: Partial<Prospect>): InternalState {
  return {
    firstName: p?.firstName ?? "",
    lastName: p?.lastName ?? "",
    email: p?.email ?? "",
    phone: p?.phone ?? "",
    source: p?.source ?? "Power Coaching",
    status: (p?.status as ProspectStatus) ?? "a_contacter",
    productInterest: p?.productInterest ?? "",
    context: p?.context ?? "",
    nextFollowUpAt: p?.nextFollowUpAt ?? "",
  };
}

function toPayload(s: InternalState): Partial<NewProspect> {
  return {
    firstName: s.firstName.trim(),
    lastName: s.lastName.trim() || null,
    email: s.email.trim() || null,
    phone: s.phone.trim() || null,
    source: s.source.trim() || null,
    status: s.status,
    productInterest: s.productInterest || null,
    context: s.context.trim() || null,
    nextFollowUpAt: s.nextFollowUpAt || null,
  };
}

interface Props {
  initial?: Partial<Prospect>;
  onSubmit: (values: Partial<NewProspect>) => Promise<void> | void;
  submitLabel?: string;
  formId?: string;
}

export function ProspectForm({
  initial,
  onSubmit,
  submitLabel = "Enregistrer",
  formId = "prospect-form",
}: Props) {
  const [state, setState] = useState<InternalState>(toState(initial));
  const [submitting, setSubmitting] = useState(false);
  const contextField = useRef<HTMLTextAreaElement>(null);
  const [error, setError] = useState<string | null>(null);

  function update<K extends keyof InternalState>(
    key: K,
    value: InternalState[K],
  ) {
    setState((v) => ({ ...v, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!state.firstName.trim()) {
      setError("Prénom requis");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // Valeur réelle du champ : un outil de dictée peut y écrire sans prévenir React.
      await onSubmit(toPayload({ ...state, context: contextField.current?.value ?? state.context }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Prénom" required>
          <input
            className="input-base"
            value={state.firstName}
            onChange={(e) => update("firstName", e.target.value)}
            autoFocus
            required
          />
        </Field>
        <Field label="Nom">
          <input
            className="input-base"
            value={state.lastName}
            onChange={(e) => update("lastName", e.target.value)}
          />
        </Field>
        <Field label="Email">
          <input
            type="email"
            className="input-base"
            value={state.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="prenom@exemple.fr"
          />
        </Field>
        <Field label="Téléphone">
          <input
            type="tel"
            className="input-base"
            value={state.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="+33 6 xx xx xx xx"
          />
        </Field>
        <Field label="Source">
          <input
            className="input-base"
            value={state.source}
            onChange={(e) => update("source", e.target.value)}
            placeholder="Power Coaching, Instagram…"
          />
        </Field>
        <Field label="Statut">
          <select
            className="input-base"
            value={state.status}
            onChange={(e) =>
              update("status", e.target.value as ProspectStatus)
            }
          >
            {PROSPECT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {PROSPECT_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Produit visé">
          <select
            className="input-base"
            value={state.productInterest}
            onChange={(e) =>
              update(
                "productInterest",
                e.target.value as InternalState["productInterest"],
              )
            }
          >
            <option value="">Pas encore décidé</option>
            {PRODUCT_LIST.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}, {p.defaultPrice.toLocaleString("fr-FR")} €
              </option>
            ))}
          </select>
        </Field>
        <Field label="Prochaine relance">
          <input
            type="date"
            className="input-base"
            value={state.nextFollowUpAt}
            onChange={(e) => update("nextFollowUpAt", e.target.value)}
          />
        </Field>
      </div>
      <Field label="Contexte / notes">
        <DictationTextarea
          ref={contextField}
          className="input-base"
          initialValue={state.context}
          onValueChange={(v) => update("context", v)}
          placeholder="Ce qui peut t'aider à closer : situation, freins, dispos..."
          rows={4}
        />
      </Field>
      {error && (
        <p className="text-sm text-[var(--color-coral-600)]">{error}</p>
      )}
      <div className="flex items-center justify-end">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting}
        >
          {submitting ? "Enregistrement…" : submitLabel}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-[var(--color-muted-foreground)] uppercase tracking-wide">
        {label}
        {required && (
          <span className="text-[var(--color-coral-500)] ml-0.5">*</span>
        )}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
